import React from 'react';
import { Layers, Globe, GitBranch, Edit, Trash2, ChevronRight, CheckCircle, AlertTriangle, XCircle, Activity, Clock } from 'lucide-react';

const BranchCard = ({ branch, onDelete, onClick, onEdit }) => {
  const { branchName, domain, branchId, status , createdAt } = branch;
  const domainUrl = domain ? `https://${domain}` : null;

  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
          label: 'Active'
        };
      case 'pending':
        return {
          icon: <AlertTriangle className="w-4 h-4" />,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
          label: 'Pending'
        };
      case 'inactive':
        return {
          icon: <XCircle className="w-4 h-4" />,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
          label: 'Inactive'
        };
      default:
        return {
          icon: <Activity className="w-4 h-4" />,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          label: 'N/A'
        };
    }
  };

  const statusConfig = getStatusConfig(status);
  const isPending = status?.toLowerCase() === 'pending';
  return (
    <div className="relative group">
      {/* Edit and Delete buttons positioned outside and above the card */}
      { isPending && (
        <div className="absolute -top-3 -right-3 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(branchId);
            }}
            className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors duration-200"
            aria-label="Edit branch"
          >
            <Edit className="w-4 h-4 text-blue-600" />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onDelete?.(branchId);
            }}
            className="p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors duration-200"
            aria-label="Delete branch"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      )}
      {/* Card with top status border */}
      <div
        onClick={onClick}
        className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200"
      >
        {/* Top status border */}
        <div className={`h-1 w-full ${statusConfig.bgColor}`}></div>
        
        {/* Card Body */}
        <div className="p-6 space-y-4">
          {/* Header with Icon and Branch Name */}
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-50 rounded-lg group-hover:bg-indigo-100 transition-colors">
              <GitBranch className="w-5 h-5 text-indigo-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 truncate">
              {branchName}
            </h3>
          </div>

          {/* Status and Domain badges */}
          <div className="flex flex-wrap gap-3">
            {/* Status Badge */}
            <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
              {statusConfig.icon}
              <span className="ml-1">{statusConfig.label}</span>
            </div>

            {/* Domain Badge */}
            <div className="flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700">
              <Globe className="w-4 h-4 mr-1" />
              <span>Domain</span>
            </div>
          </div>

          {/* Domain URL and Created At */}
          <div className="space-y-3 text-sm text-gray-600">
            {/* Domain URL */}
            <div className="flex items-center">
              <Globe className="w-4 h-4 mr-2 text-gray-400" />
              {domainUrl ? (
                <a
                  href={domainUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-700 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {domainUrl}
                </a>
              ) : (
                <span className="text-gray-500">No domain configured</span>
              )}
            </div>

            {/* Created At info if available */}
            {createdAt && (
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-gray-400" />
                <div className="flex flex-col">
                  <p>Created at: {createdAt}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer with CTA */}
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-600">
              Branch Details
            </span>
            <ChevronRight className="w-5 h-5 text-indigo-600" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BranchCard;